<template>
  <span v-if="tooltipIsActive" class="px-1">
    <i
      class="fas text-secondary"
      :class="classNames"
      @mouseover="showOverlay"
      @touchstart.passive="showOverlay"
      @mouseleave="resetTimer"
    />
    <Popover v-if="content != null && content.length > 0" ref="op">
      <h5>{{ title }}</h5>
      <div style="font-size: 0.75em" class="pt-2">
        <div v-for="(el, index) in content" :key="index" class="pb-2">
          {{ el }}
        </div>
      </div>
    </Popover>
  </span>
</template>

<script setup lang="ts">
import { computed, inject, type PropType, ref } from "vue";
import { DateTime } from "luxon";
import { useTranslation } from "@/language/translation";
import { type EventListEventFieldsFragment, SQDCBoardIssueConnectionTypes } from "@/graphql";
import injectAsserted from "@/functions/assert/injectAsserted";
import { BoardIdInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { LISA_DATE_FULL } from "@/language/translation";
import Popover from "primevue/popover";
import { TimeZoneInjectionKey } from "@/functions/timezoneUtil";
import { getBoardNameFromCache } from "@/modules/sqdc/helper/EABConfigHelper";

const props = defineProps({
  event: {
    required: true,
    type: Object as PropType<EventListEventFieldsFragment>,
  },
});

const currentBoardId = inject(
  BoardIdInjectionKey,
  computed(() => null),
);

const ownBoardConnection = computed(() =>
  props.event.boardConnection.find((con) => con.boardId === currentBoardId.value),
);

const isDelegatedOrEscalated = computed(() => props.event.boardConnection.length > 1);

const classNames = computed(() => {
  if (!isDelegatedOrEscalated.value || currentBoardId.value == null) {
    return "";
  }
  switch (ownBoardConnection.value?.status) {
    case SQDCBoardIssueConnectionTypes.delegate:
    case SQDCBoardIssueConnectionTypes.escalate:
      return "fa-arrow-alt-circle-down";
    case SQDCBoardIssueConnectionTypes.created:
      return "fa-arrow-alt-circle-up";
    default:
      return "";
  }
});

const tooltipIsActive = computed(() => {
  return classNames.value !== "";
});

const { t } = useTranslation();

const title = computed(() => {
  if (!isDelegatedOrEscalated.value || ownBoardConnection.value == null) {
    return "";
  }
  switch (ownBoardConnection.value?.status) {
    case SQDCBoardIssueConnectionTypes.delegate:
    case SQDCBoardIssueConnectionTypes.escalate:
      return t("components.event-action.issue.BoardOriginArrow.otherOrigin");
    case SQDCBoardIssueConnectionTypes.created:
      return t("components.event-action.issue.BoardOriginArrow.sameOrigin");
    default:
      return "";
  }
});

const tz = injectAsserted(TimeZoneInjectionKey);

function formatDate(date: string) {
  let luxon = DateTime.fromISO(date).setZone(tz.value);
  return luxon.toLocaleString(LISA_DATE_FULL);
}

const content = computed(() => {
  if (!isDelegatedOrEscalated.value) {
    return [];
  }
  return props.event.boardConnection?.map((con) => {
    const reason =
      con?.reason != null && con.reason.length > 0
        ? t("components.event-action.issue.BoardOriginArrow.reason", {
            reason: con.reason,
          })
        : "";
    let result: string;
    switch (con?.status) {
      case SQDCBoardIssueConnectionTypes.delegate:
        if (currentBoardId.value === con.boardId) {
          result = t("components.event-action.issue.BoardOriginArrow.delegatedTextOwn", {
            createdAt: formatDate(con.createdAt),
          }).toString();
          break;
        }
        result = t("components.event-action.issue.BoardOriginArrow.delegatedText", {
          createdAt: formatDate(con.createdAt),
          boardName: getBoardNameFromCache(con.boardId),
        }).toString();
        break;
      case SQDCBoardIssueConnectionTypes.escalate:
        if (currentBoardId.value === con.boardId) {
          result = t("components.event-action.issue.BoardOriginArrow.escalatedTextOwn", {
            createdAt: formatDate(con.createdAt),
          }).toString();
          break;
        }
        result = t("components.event-action.issue.BoardOriginArrow.escalatedText", {
          createdAt: formatDate(con.createdAt),
          boardName: getBoardNameFromCache(con.boardId),
        }).toString();
        break;
      case SQDCBoardIssueConnectionTypes.created:
        if (currentBoardId.value === con.boardId) {
          result = t("components.event-action.issue.BoardOriginArrow.createdTextOwn", {
            createdAt: formatDate(con.createdAt),
          }).toString();
          break;
        }
        result = t("components.event-action.issue.BoardOriginArrow.createdText", {
          createdAt: formatDate(con.createdAt),
          boardName: getBoardNameFromCache(con.boardId),
        }).toString();
        break;
      default:
        return "";
    }
    return `${result} ${reason}`;
  });
});

const op = ref<InstanceType<typeof Popover>>();
const timer = ref<number>();

function showOverlay(event: Event) {
  clearTimeout(timer.value);
  op.value?.show(event);
}
function hideOverlay() {
  op.value?.hide();
}
function resetTimer() {
  timer.value = setTimeout(() => {
    hideOverlay();
  }, 700);
}
</script>

<style lang="scss" scoped>
h5 {
  margin-top: 0;
  margin-bottom: 0;
}
</style>
